import { Injectable } from "@angular/core";
import { PercentageBarModel, PercentageBarSegment } from "@app/directives/_layout/v-table/_components/cells/percentage-bar/percentage-bar-cell.component";
import { TranslationService } from "@app/services/translation.service";
import { CalcType, FtCloudConfigExceptionError } from "../../../_enums/camera-configs/CameraConfigEnums";
import { IAccountGroupConfigDto } from "../../../_models/_camera-configs/CameraConfigModels";

@Injectable({
    providedIn: 'root'
})
export class StreamaxConfigsPercentageBarService {
    private readonly maxErrors = Object.keys(FtCloudConfigExceptionError).filter(key => isNaN(Number(key))).length - 1;

    constructor(private readonly translationService: TranslationService) { }

    getPercentageBarModel(accountGroupDto: IAccountGroupConfigDto, calcType: CalcType): PercentageBarModel {
        switch (calcType) {
            case CalcType.DeviceComms:
                return this.calculateDeviceCommsPercentages(accountGroupDto);
            case CalcType.TotalErrors:
                return this.calculateTotalErrorsPercentages(accountGroupDto);
            case CalcType.TotalSync:
                return this.calculateTotalSyncPercentages(accountGroupDto);
            default:
                throw new Error("Invalid calcType");
        }
    }
    
    private calculateDeviceCommsPercentages(dto: IAccountGroupConfigDto): PercentageBarModel {
        const positiveSegment = this.getSegment(
            dto.PeripheralWithConfigRecord.filter(r => !!r.LatestRecord).length,
            'StreamaxConfigs.Collected'
        );

        const negativeSegment = this.getSegment(
            dto.PeripheralWithConfigRecord.filter(r => !r.LatestRecord).length,
            'StreamaxConfigs.Uncollected'
        )

        const mainText = this.translationService.t('StreamaxConfigs.NConfigsCollected', positiveSegment.count);

        return { positiveSegment, negativeSegment, mainText };
    }
    
    private calculateTotalErrorsPercentages(dto: IAccountGroupConfigDto): PercentageBarModel {
        const totalPossibleErrorsOverall = dto.PeripheralWithConfigRecord.length * this.maxErrors;
        const totalPossibleErrorsReceived = dto.PeripheralWithConfigRecord.filter(r => !!r.LatestRecord).length * this.maxErrors;

        const positiveSegment = this.getSegment(totalPossibleErrorsReceived - dto.CombinedErrors, 'StreamaxConfigs.MatchedProps');
        const negativeSegment = this.getSegment(dto.CombinedErrors, 'StreamaxConfigs.UnMatchedProps');
        const neutralSegment = this.getSegment(dto.PeripheralWithConfigRecord.filter(r => !r.LatestRecord).length * this.maxErrors, 'Common.Unknown');

        const mainText = this.translationService.t(
            'StreamaxConfigs.XOfYMetricsAdhere', 
            [totalPossibleErrorsReceived - dto.CombinedErrors, totalPossibleErrorsOverall]
        );

        return { positiveSegment, negativeSegment, neutralSegment, mainText };
    }
    
    private calculateTotalSyncPercentages(dto: IAccountGroupConfigDto): PercentageBarModel {
        const positiveSegment = this.getSegment(
            dto.PeripheralWithConfigRecord.filter(r =>
                r.LatestRecord?.CameraDeviceParameterSettings?.ProfileExceptionErrors?.Errors.length === 0
            ).length,
            'StreamaxConfigs.MatchedDevices'
        );

        const negativeSegment = this.getSegment(
            dto.PeripheralWithConfigRecord.filter(r =>
                r.LatestRecord?.CameraDeviceParameterSettings?.ProfileExceptionErrors?.Errors.length > 0
            ).length,
            'StreamaxConfigs.UnmatchedDevices'
        );

        const neutralSegment = this.getSegment(dto.PeripheralWithConfigRecord.filter(r => !r.LatestRecord).length, 'StreamaxConfigs.Uncollected');

        const mainText = this.translationService.t('StreamaxConfigs.NDevicesMatchCompletely', positiveSegment.count);

        return { positiveSegment, negativeSegment, neutralSegment, mainText };
    }
    
    private getSegment(count: number, tKey: string): PercentageBarSegment {
        return {
            count,
            tooltip: this.translationService.t(tKey)
        }
    }
}
